import {
  getClients,
} from "../../services/adminApi";


/**
 * Retail POS Customer Runtime Adapter.
 *
 * Adapter only.
 *
 * Authority remains:
 *
 *   /api/admin/clients
 *
 * It owns:
 *
 * - no Customer persistence;
 * - no Customer domain model;
 * - no company scope;
 * - no activity scope;
 * - no Customer state.
 *
 * The existing backend currently exposes a company-scoped
 * collection but no dedicated operator Customer search route.
 *
 * Text filtering therefore remains an adapter concern over the
 * authoritative collection and does not create a parallel
 * Customer Engine.
 */
export function createRetailPOSCustomerRuntime({
  customerApi = null,
} = {}) {
  const api =
    customerApi
    ?? {
      getClients,
    };

  assertCustomerApi(
    api
  );

  return Object.freeze({
    async resolveCustomer(
      reference,
      context = {}
    ) {
      const companyUuid =
        requireCompanyUuid(
          context
        );

      const customerUuid =
        resolveCustomerUuid(
          reference
        );

      const customers =
        await loadCustomers(
          api,
          companyUuid
        );

      return (
        customers.find(
          (customer) =>
            normalizeString(
              customer?.uuid
              ?? customer?.client_uuid
            )
            === customerUuid
        )
        ?? null
      );
    },


    async searchCustomers(
      query,
      context = {}
    ) {
      const companyUuid =
        requireCompanyUuid(
          context
        );

      const normalizedQuery =
        normalizeSearchQuery(
          query
        );

      const customers =
        await loadCustomers(
          api,
          companyUuid
        );

      return customers.filter(
        (customer) =>
          getSearchableCustomerValues(
            customer
          ).some(
            (value) =>
              value.includes(
                normalizedQuery
              )
          )
      );
    },
  });
}


export function isRetailPOSCustomerRuntime(
  runtime
) {
  return Boolean(
    runtime
    && typeof runtime
      === "object"
    && typeof runtime.resolveCustomer
      === "function"
    && typeof runtime.searchCustomers
      === "function"
  );
}


async function loadCustomers(
  api,
  companyUuid
) {
  const response =
    await api.getClients({
      companyUuid,
    });

  const payload =
    unwrapResponseData(
      response
    );

  if (Array.isArray(payload)) {
    return payload;
  }

  if (
    payload
    && Array.isArray(
      payload.clients
    )
  ) {
    return payload.clients;
  }

  return [];
}


function resolveCustomerUuid(
  reference
) {
  const raw =
    typeof reference
      === "string"
      ? reference
      : (
          reference?.uuid
          ?? reference?.client_uuid
        );

  const uuid =
    normalizeString(
      raw
    );

  if (!uuid) {
    throw new TypeError(
      "Customer reference must expose a non-empty uuid."
    );
  }

  return uuid;
}


function getSearchableCustomerValues(
  customer
) {
  const fullName = [
    customer?.firstname,
    customer?.lastname,
  ]
    .filter(Boolean)
    .join(" ");

  return [
    customer?.uuid,
    customer?.client_uuid,
    customer?.name,
    fullName,
    customer?.firstname,
    customer?.lastname,
    customer?.email,
    customer?.phone_number,
  ]
    .map(
      normalizeString
    )
    .filter(Boolean);
}


function normalizeSearchQuery(
  value
) {
  const query =
    normalizeString(
      value
    );

  if (!query) {
    throw new TypeError(
      "Customer search query must be a non-empty string."
    );
  }

  return query;
}


function normalizeString(
  value
) {
  if (
    value === null
    || value === undefined
  ) {
    return "";
  }

  return String(value)
    .trim()
    .toLocaleLowerCase();
}


function requireCompanyUuid(
  context
) {
  const companyUuid =
    typeof context?.companyUuid
      === "string"
      ? context.companyUuid.trim()
      : "";

  if (!companyUuid) {
    throw new TypeError(
      "companyUuid must be a non-empty string."
    );
  }

  return companyUuid;
}


function unwrapResponseData(
  response
) {
  if (
    response
    && typeof response
      === "object"
    && Object.prototype
      .hasOwnProperty.call(
        response,
        "data"
      )
  ) {
    return response.data;
  }

  return response;
}


function assertCustomerApi(
  api
) {
  if (
    !api
    || typeof api
      !== "object"
    || typeof api.getClients
      !== "function"
  ) {
    throw new TypeError(
      "customerApi must expose getClients()."
    );
  }
}
